import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react';
import { createPortal } from 'react-dom';
import {
  IconButton,
  IconXLine,
  IconMiniArrowDownLine,
  IconMiniArrowUpLine,
  IconDragHandleLine,
  IconFullScreenLine,
  IconExitFullScreenLine,
  Text,
  Flex
} from '@smarterservices/smarter-ui';
import { useHotkeys } from 'react-hotkeys-hook';
import { DraggableModalProps, Position, Size, PositionPreset } from './types';
import ModalManager from './ModalManager';
import { DraggableModalProvider, DraggableModalContextType } from './DraggableModalContext';

export interface DraggableModalRef {
  bringToFront: () => void;
  minimize: () => void;
  maximize: () => void;
  toggleMinimize: () => void;
  updatePosition: (position: Position) => void;
  updateSize: (size: Size) => void;
  getPosition: () => Position;
  getSize: () => Size;
  isMinimized: () => boolean;
}

// Helper function to convert size value to pixels
const convertSizeToPixels = (
  value: number | string,
  dimension: 'width' | 'height',
  viewportWidth: number,
  viewportHeight: number
): number => {
  if (typeof value === 'number') {
    return value;
  }

  // Handle string values
  const stringValue = value.trim();

  // Handle percentage
  if (stringValue.endsWith('%')) {
    const percentage = parseFloat(stringValue) / 100;
    return dimension === 'width'
      ? viewportWidth * percentage
      : viewportHeight * percentage;
  }

  // Handle vh
  if (stringValue.endsWith('vh')) {
    const vh = parseFloat(stringValue);
    return (viewportHeight / 100) * vh;
  }

  // Handle vw
  if (stringValue.endsWith('vw')) {
    const vw = parseFloat(stringValue);
    return (viewportWidth / 100) * vw;
  }

  // Handle px (explicit)
  if (stringValue.endsWith('px')) {
    return parseFloat(stringValue);
  }

  // Handle calc() - create a temporary element to compute the value
  if (stringValue.startsWith('calc(')) {
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style[dimension] = stringValue;
    document.body.appendChild(tempDiv);
    const computedValue = dimension === 'width'
      ? tempDiv.offsetWidth
      : tempDiv.offsetHeight;
    document.body.removeChild(tempDiv);
    return computedValue;
  }

  // Default: try to parse as number
  const parsed = parseFloat(stringValue);
  return isNaN(parsed) ? (dimension === 'width' ? 800 : 600) : parsed;
};

// Helper function to calculate position from preset string
const calculatePositionFromPreset = (
  preset: PositionPreset,
  modalSize: Size,
  constrainToContainer: boolean,
  containerElement?: HTMLElement
): Position => {
  // Get viewport dimensions
  let viewportWidth = window.innerWidth;
  let viewportHeight = window.innerHeight;

  // Use container dimensions if constrained
  if (constrainToContainer) {
    const container = containerElement || document.querySelector('.session-viewer-container') as HTMLElement;
    if (container) {
      const containerRect = container.getBoundingClientRect();
      viewportWidth = containerRect.width;
      viewportHeight = containerRect.height;
    }
  }

  // Convert modal size to pixels
  const modalWidth = convertSizeToPixels(modalSize.width, 'width', viewportWidth, viewportHeight);
  const modalHeight = convertSizeToPixels(modalSize.height, 'height', viewportWidth, viewportHeight);

  const padding = 10; // 10px padding from edges

  // Parse the preset string
  const [vertical, horizontal] = preset.split(' ');

  let x = padding;
  let y = padding;

  // Calculate horizontal position
  if (horizontal === 'left') {
    x = padding;
  } else if (horizontal === 'center') {
    x = (viewportWidth - modalWidth) / 2;
  } else if (horizontal === 'right') {
    x = viewportWidth - modalWidth - padding;
  }

  // Calculate vertical position
  if (vertical === 'top') {
    y = padding;
  } else if (vertical === 'left' || vertical === 'right') {
    // For 'left center' and 'right center'
    y = (viewportHeight - modalHeight) / 2;
  } else if (vertical === 'bottom') {
    y = viewportHeight - modalHeight - padding;
  }

  return { x: Math.max(0, x), y: Math.max(0, y) };
};

// Helper function to calculate centered position and adjusted size
const calculateCenteredPositionAndSize = (
  initialSize: Size,
  constrainToContainer: boolean,
  containerElement?: HTMLElement
): { position: Position; size: Size } => {
  // Get viewport dimensions
  let viewportWidth = window.innerWidth;
  let viewportHeight = window.innerHeight;

  // Use container dimensions if constrained
  if (constrainToContainer) {
    const container = containerElement || document.querySelector('.session-viewer-container') as HTMLElement;
    if (container) {
      const containerRect = container.getBoundingClientRect();
      viewportWidth = containerRect.width;
      viewportHeight = containerRect.height;
    }
  }

  // Convert initial size to pixels for calculations
  const widthPx = convertSizeToPixels(initialSize.width, 'width', viewportWidth, viewportHeight);
  const heightPx = convertSizeToPixels(initialSize.height, 'height', viewportWidth, viewportHeight);

  // Calculate adjusted size to fit within viewport with some padding
  const padding = 40; // 20px padding on each side
  const maxWidth = viewportWidth - padding;
  const maxHeight = viewportHeight - padding;

  const adjustedSize = {
    width: Math.min(widthPx, maxWidth),
    height: Math.min(heightPx, maxHeight)
  };

  // Calculate centered position
  const centeredPosition = {
    x: Math.max(0, (viewportWidth - adjustedSize.width) / 2),
    y: Math.max(0, (viewportHeight - adjustedSize.height) / 2)
  };

  return { position: centeredPosition, size: adjustedSize };
};

const DraggableModal = forwardRef<DraggableModalRef, DraggableModalProps>(({
  isOpen,
  onClose,
  title,
  children,
  initialPosition,
  initialSize = { width: 800, height: 600 },
  resizable = true,
  minimizable = true,
  initialMinimized = false,
  minSize = { width: 400, height: 300 },
  maxSize,
  className = '',
  style = {},
  headerStyle = {},
  contentStyle = {},
  showDragHandle = false,
  dragHandleIcon,
  closeIcon,
  minimizeIcon,
  maximizeIcon,
  onDrag,
  onResize,
  onMinimize,
  minimizeBehavior = 'dock',
  constrainToContainer = false,
  containerElement,
  keyboardShortcuts = {
    close: 'escape',
    minimize: 'ctrl+m'
  },
  autoFocus = true,
  zIndexBase = 8000,
  id,
  mountNode,
  maintainPositionOnClose = false,
  showMinMax = false
}, ref) => {
  // Generate unique ID if not provided
  const modalId = useRef(id || `modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`).current;

  // Store initial position and size for reset
  const initialPositionAndSize = useRef<{ position: Position; size: Size } | null>(null);

  // Calculate initial position and size
  const getInitialPositionAndSize = useCallback(() => {
    if (initialPosition) {
      // Check if initialPosition is a string preset
      if (typeof initialPosition === 'string') {
        const position = calculatePositionFromPreset(
          initialPosition as PositionPreset,
          initialSize,
          constrainToContainer,
          containerElement
        );
        return { position, size: initialSize };
      }
      // Otherwise it's a Position object
      return { position: initialPosition, size: initialSize };
    }
    return calculateCenteredPositionAndSize(initialSize, constrainToContainer, containerElement);
  }, [initialPosition, initialSize, constrainToContainer, containerElement]);

  // Initialize position and size
  if (!initialPositionAndSize.current) {
    initialPositionAndSize.current = getInitialPositionAndSize();
  }

  const [position, setPosition] = useState<Position>(() => initialPositionAndSize.current!.position);
  const [size, setSize] = useState<Size>(() => initialPositionAndSize.current!.size);
  const [isMinimized, setIsMinimized] = useState(initialMinimized);
  const [originalPosition, setOriginalPosition] = useState<Position | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(title);
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({
    x: 0, y: 0, width: 0, height: 0, posX: 0, posY: 0
  });
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const [zIndex, setZIndex] = useState(zIndexBase);
  const [isMaximized, setIsMaximized] = useState(false);
  const preMaximizeState = useRef<{ position: Position; size: Size } | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  const modalManager = ModalManager.getInstance();

  // Register/unregister modal and manage z-index
  useEffect(() => {
    if (isOpen) {
      const newZIndex = modalManager.registerModal(modalId);
      setZIndex(newZIndex);
    } else {
      modalManager.unregisterModal(modalId);

      // Reset position and size to initial values when closing if maintainPositionOnClose is false
      if (!maintainPositionOnClose && initialPositionAndSize.current) {
        setPosition(initialPositionAndSize.current.position);
        setSize(initialPositionAndSize.current.size);
        setIsMaximized(false);
        preMaximizeState.current = null;
      }
    }

    return () => {
      if (isOpen) {
        modalManager.unregisterModal(modalId);
      }
    };
  }, [isOpen, modalId, modalManager, maintainPositionOnClose]);

  // Update title when prop changes
  useEffect(() => {
    setCurrentTitle(title);
  }, [title]);

  // Bring modal to front when clicked
  const handleModalClick = useCallback(() => {
    if (isOpen && !modalManager.isActive(modalId)) {
      const newZIndex = modalManager.bringToFront(modalId);
      setZIndex(newZIndex);
    }
  }, [isOpen, modalId, modalManager]);

  // Add scrollbar styles to document head
  useEffect(() => {
    const styleId = 'draggable-modal-scrollbar-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .draggable-modal-content::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .draggable-modal-content::-webkit-scrollbar-track {
          background: #f5f5f5;
          border-radius: 4px;
        }
        .draggable-modal-content::-webkit-scrollbar-thumb {
          background: #c7cdd1;
          border-radius: 4px;
        }
        .draggable-modal-content::-webkit-scrollbar-thumb:hover {
          background: #a0a8b0;
        }
        .draggable-modal-content::-webkit-scrollbar-corner {
          background: #f5f5f5;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Handle dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't start dragging if clicking on buttons or resize handles
    if (
      e.target instanceof HTMLElement && (
        e.target.closest('button') ||
        e.target.closest('[role="button"]') ||
        e.target.classList.contains('resize-handle')
      )
    ) {
      return;
    }

    handleModalClick(); // Bring to front
    setIsDragging(true);
    isDraggingRef.current = true;
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  }, [position, handleModalClick]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    let finalPosition = { x: newX, y: newY };

    // Only apply constraints if constrainToContainer is true
    if (constrainToContainer) {
      // Get container bounds for constraint
      let maxX = window.innerWidth - 200;
      let maxY = window.innerHeight - 100;

      const container = containerElement || document.querySelector('.session-viewer-container') as HTMLElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        maxX = containerRect.width - 200;
        maxY = containerRect.height - 100;
      }

      finalPosition = {
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      };
    }

    setPosition(finalPosition);
    onDrag?.(finalPosition);
  }, [dragStart, constrainToContainer, containerElement, onDrag]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
  }, []);

  // Handle resizing
  const handleResizeMouseDown = useCallback((e: React.MouseEvent, direction: string) => {
    handleModalClick(); // Bring to front
    setIsResizing(true);
    isResizingRef.current = true;
    setResizeDirection(direction);

    // Convert string sizes to pixels for resize calculations
    const widthPx = typeof size.width === 'number'
      ? size.width
      : convertSizeToPixels(size.width, 'width', window.innerWidth, window.innerHeight);
    const heightPx = typeof size.height === 'number'
      ? size.height
      : convertSizeToPixels(size.height, 'height', window.innerWidth, window.innerHeight);

    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: widthPx,
      height: heightPx,
      posX: position.x,
      posY: position.y
    });
    e.preventDefault();
    e.stopPropagation();
  }, [size, position, handleModalClick]);

  const handleResizeMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizingRef.current || !resizeDirection) return;

    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;

    // Convert minSize and maxSize to pixels
    const minWidthPx = typeof minSize.width === 'number'
      ? minSize.width
      : convertSizeToPixels(minSize.width, 'width', window.innerWidth, window.innerHeight);
    const minHeightPx = typeof minSize.height === 'number'
      ? minSize.height
      : convertSizeToPixels(minSize.height, 'height', window.innerWidth, window.innerHeight);

    let maxWidthPx: number | undefined;
    let maxHeightPx: number | undefined;
    if (maxSize) {
      maxWidthPx = typeof maxSize.width === 'number'
        ? maxSize.width
        : convertSizeToPixels(maxSize.width, 'width', window.innerWidth, window.innerHeight);
      maxHeightPx = typeof maxSize.height === 'number'
        ? maxSize.height
        : convertSizeToPixels(maxSize.height, 'height', window.innerWidth, window.innerHeight);
    }

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;
    let newX = resizeStart.posX;
    let newY = resizeStart.posY;

    // Handle different resize directions
    switch (resizeDirection) {
      case 'n': // North (top edge)
        newHeight = Math.max(minHeightPx, resizeStart.height - deltaY);
        newY = resizeStart.posY + deltaY;
        if (newHeight === minHeightPx) newY = resizeStart.posY + (resizeStart.height - minHeightPx);
        break;
      case 's': // South (bottom edge)
        newHeight = Math.max(minHeightPx, resizeStart.height + deltaY);
        break;
      case 'e': // East (right edge)
        newWidth = Math.max(minWidthPx, resizeStart.width + deltaX);
        break;
      case 'w': // West (left edge)
        newWidth = Math.max(minWidthPx, resizeStart.width - deltaX);
        newX = resizeStart.posX + deltaX;
        if (newWidth === minWidthPx) newX = resizeStart.posX + (resizeStart.width - minWidthPx);
        break;
      case 'ne': // Northeast (top-right corner)
        newWidth = Math.max(minWidthPx, resizeStart.width + deltaX);
        newHeight = Math.max(minHeightPx, resizeStart.height - deltaY);
        newY = resizeStart.posY + deltaY;
        if (newHeight === minHeightPx) newY = resizeStart.posY + (resizeStart.height - minHeightPx);
        break;
      case 'nw': // Northwest (top-left corner)
        newWidth = Math.max(minWidthPx, resizeStart.width - deltaX);
        newHeight = Math.max(minHeightPx, resizeStart.height - deltaY);
        newX = resizeStart.posX + deltaX;
        newY = resizeStart.posY + deltaY;
        if (newWidth === minWidthPx) newX = resizeStart.posX + (resizeStart.width - minWidthPx);
        if (newHeight === minHeightPx) newY = resizeStart.posY + (resizeStart.height - minHeightPx);
        break;
      case 'se': // Southeast (bottom-right corner)
        newWidth = Math.max(minWidthPx, resizeStart.width + deltaX);
        newHeight = Math.max(minHeightPx, resizeStart.height + deltaY);
        break;
      case 'sw': // Southwest (bottom-left corner)
        newWidth = Math.max(minWidthPx, resizeStart.width - deltaX);
        newHeight = Math.max(minHeightPx, resizeStart.height + deltaY);
        newX = resizeStart.posX + deltaX;
        if (newWidth === minWidthPx) newX = resizeStart.posX + (resizeStart.width - minWidthPx);
        break;
    }

    // Apply max size constraints
    if (maxWidthPx !== undefined && maxHeightPx !== undefined) {
      newWidth = Math.min(newWidth, maxWidthPx);
      newHeight = Math.min(newHeight, maxHeightPx);
    }

    const newSize = { width: newWidth, height: newHeight };
    const newPosition = { x: newX, y: newY };

    setSize(newSize);
    setPosition(newPosition);
    onResize?.(newSize);
  }, [resizeStart, resizeDirection, minSize, maxSize, onResize]);

  const handleResizeMouseUp = useCallback(() => {
    setIsResizing(false);
    isResizingRef.current = false;
    setResizeDirection(null);
  }, []);

  // Event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'move';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);

      // Set cursor based on resize direction
      const cursorMap: Record<string, string> = {
        'n': 'ns-resize',
        's': 'ns-resize',
        'e': 'ew-resize',
        'w': 'ew-resize',
        'ne': 'nesw-resize',
        'nw': 'nwse-resize',
        'se': 'nwse-resize',
        'sw': 'nesw-resize'
      };
      document.body.style.cursor = cursorMap[resizeDirection || ''] || 'se-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, resizeDirection, handleResizeMouseMove, handleResizeMouseUp]);

  const handleMinimize = useCallback(() => {
    const newMinimized = !isMinimized;

    if (minimizeBehavior === 'dock') {
      if (newMinimized) {
        // Store original position and move to bottom
        setOriginalPosition(position);

        // Convert size.height to pixels if it's a string
        const heightPx = typeof size.height === 'number'
          ? size.height
          : convertSizeToPixels(size.height, 'height', window.innerWidth, window.innerHeight);

        const dockedY = position.y + heightPx - 60; // 60px is approximate header height
        setPosition({ x: position.x, y: dockedY });
      } else {
        // Restore and center on screen
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const widthPx = typeof size.width === 'number'
          ? size.width
          : convertSizeToPixels(size.width, 'width', viewportWidth, viewportHeight);
        const heightPx2 = typeof size.height === 'number'
          ? size.height
          : convertSizeToPixels(size.height, 'height', viewportWidth, viewportHeight);
        setPosition({
          x: Math.max(0, (viewportWidth - widthPx) / 2),
          y: Math.max(0, (viewportHeight - heightPx2) / 2)
        });
        setOriginalPosition(null);
      }
    }

    setIsMinimized(newMinimized);
    onMinimize?.(newMinimized);
  }, [isMinimized, onMinimize, minimizeBehavior, position, size, originalPosition]);

  const handleToggleMaximize = useCallback(() => {
    if (isMaximized) {
      // Restore to pre-maximize state
      if (preMaximizeState.current) {
        setPosition(preMaximizeState.current.position);
        setSize(preMaximizeState.current.size);
        preMaximizeState.current = null;
      } else {
        // Fallback to initialSize centered
        const initPosSize = calculateCenteredPositionAndSize(initialSize, constrainToContainer, containerElement);
        setPosition(initPosSize.position);
        setSize(initPosSize.size);
      }
      setIsMaximized(false);
    } else {
      // Store current position/size before maximizing
      preMaximizeState.current = { position, size };

      // Calculate maximized size — use maxSize if provided, otherwise fill browser window
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const maxW = maxSize?.width
        ? (typeof maxSize.width === 'number' ? maxSize.width : convertSizeToPixels(maxSize.width, 'width', viewportWidth, viewportHeight))
        : viewportWidth;
      const maxH = maxSize?.height
        ? (typeof maxSize.height === 'number' ? maxSize.height : convertSizeToPixels(maxSize.height, 'height', viewportWidth, viewportHeight))
        : viewportHeight;

      // Clamp to actual browser window
      const finalW = Math.min(maxW, viewportWidth);
      const finalH = Math.min(maxH, viewportHeight);

      // Center within viewport
      setPosition({
        x: Math.max(0, (viewportWidth - finalW) / 2),
        y: Math.max(0, (viewportHeight - finalH) / 2)
      });
      setSize({ width: finalW, height: finalH });
      setIsMaximized(true);
    }
  }, [isMaximized, position, size, initialSize, maxSize, constrainToContainer, containerElement]);

  // Keyboard shortcuts
  useHotkeys(keyboardShortcuts.close || 'escape', onClose, { enabled: isOpen });
  useHotkeys(keyboardShortcuts.minimize || 'ctrl+m', handleMinimize, { enabled: isOpen });

  // Create context value for child components
  const contextValue: DraggableModalContextType = useMemo(() => ({
    close: onClose,
    minimize: () => {
      if (!isMinimized) {
        handleMinimize();
      }
    },
    maximize: () => {
      if (isMinimized) {
        handleMinimize();
      }
    },
    toggleMinimize: handleMinimize,
    bringToFront: () => {
      if (isOpen) {
        const newZIndex = modalManager.bringToFront(modalId);
        setZIndex(newZIndex);
      }
    },
    isMinimized: () => isMinimized,
    getPosition: () => position,
    getSize: () => size,
    updatePosition: (newPosition: Position) => {
      setPosition(newPosition);
      onDrag?.(newPosition);
    },
    updateSize: (newSize: Size) => {
      setSize(newSize);
      onResize?.(newSize);
    },
    setTitle: (newTitle: string) => {
      setCurrentTitle(newTitle);
    }
  }), [onClose, isMinimized, handleMinimize, isOpen, modalManager, modalId, position, size, onDrag, onResize]);

  // Expose methods through ref
  useImperativeHandle(ref, () => ({
    bringToFront: contextValue.bringToFront,
    minimize: contextValue.minimize,
    maximize: contextValue.maximize,
    toggleMinimize: contextValue.toggleMinimize,
    updatePosition: contextValue.updatePosition,
    updateSize: contextValue.updateSize,
    getPosition: contextValue.getPosition,
    getSize: contextValue.getSize,
    isMinimized: contextValue.isMinimized
  }), [contextValue]);

  const modalStyle = useMemo(() => ({
    position: 'absolute' as const,
    left: `${position.x}px`,
    top: `${position.y}px`,
    width: typeof size.width === 'number' ? `${size.width}px` : size.width,
    height: isMinimized ? 'auto' : (typeof size.height === 'number' ? `${size.height}px` : size.height),
    zIndex,
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    border: '1px solid #c7cdd1',
    overflow: 'hidden',
    transition: isMinimized ? 'height 0.2s ease' : 'none',
    maxWidth: '100%',
    maxHeight: '100%',
    ...style
  }), [position, size, isMinimized, zIndex, style]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      style={modalStyle}
      ref={modalRef}
      className={`draggable-modal ${className}`}
      onClick={handleModalClick}
    >
      {/* Resize Handles */}
      {resizable && !isMinimized && (
        <>
          {/* Edges */}
          <div className="resize-handle" style={{ position: 'absolute', top: '-3px', left: '10px', right: '10px', height: '6px', cursor: 'ns-resize', backgroundColor: 'transparent', zIndex: 1000 }} onMouseDown={(e) => handleResizeMouseDown(e, 'n')} />
          <div className="resize-handle" style={{ position: 'absolute', bottom: '-3px', left: '10px', right: '10px', height: '6px', cursor: 'ns-resize', backgroundColor: 'transparent', zIndex: 1000 }} onMouseDown={(e) => handleResizeMouseDown(e, 's')} />
          <div className="resize-handle" style={{ position: 'absolute', left: '-3px', top: '10px', bottom: '10px', width: '6px', cursor: 'ew-resize', backgroundColor: 'transparent', zIndex: 1000 }} onMouseDown={(e) => handleResizeMouseDown(e, 'w')} />
          <div className="resize-handle" style={{ position: 'absolute', right: '-3px', top: '10px', bottom: '10px', width: '6px', cursor: 'ew-resize', backgroundColor: 'transparent', zIndex: 1000 }} onMouseDown={(e) => handleResizeMouseDown(e, 'e')} />

          {/* Corners */}
          <div className="resize-handle" style={{ position: 'absolute', top: '-5px', left: '-5px', width: '10px', height: '10px', cursor: 'nwse-resize', backgroundColor: 'transparent', zIndex: 1001 }} onMouseDown={(e) => handleResizeMouseDown(e, 'nw')} />
          <div className="resize-handle" style={{ position: 'absolute', top: '-5px', right: '-5px', width: '10px', height: '10px', cursor: 'nesw-resize', backgroundColor: 'transparent', zIndex: 1001 }} onMouseDown={(e) => handleResizeMouseDown(e, 'ne')} />
          <div className="resize-handle" style={{ position: 'absolute', bottom: '-5px', left: '-5px', width: '10px', height: '10px', cursor: 'nesw-resize', backgroundColor: 'transparent', zIndex: 1001 }} onMouseDown={(e) => handleResizeMouseDown(e, 'sw')} />
          <div className="resize-handle" style={{ position: 'absolute', bottom: '-5px', right: '-5px', width: '10px', height: '10px', cursor: 'nwse-resize', backgroundColor: 'transparent', zIndex: 1001 }} onMouseDown={(e) => handleResizeMouseDown(e, 'se')} />
        </>
      )}

      {/* Header */}
      <div
        ref={headerRef}
        style={{
          backgroundColor: '#394B59',
          padding: '8px 10px',
          borderBottom: '1px solid #c7cdd1',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          transition: isDragging ? 'none' : 'background-color 0.2s ease',
          ...headerStyle
        }}
        onMouseDown={handleMouseDown}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = '#2d3b45';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = '#394B59';
          }
        }}
      >
        <Flex justifyItems="space-between" alignItems="center">
          <Flex.Item shouldGrow>
            <Flex alignItems="center">
              {showDragHandle && (
                <Flex.Item margin="none small none none">
                  {dragHandleIcon || <IconDragHandleLine size="small" />}
                </Flex.Item>
              )}
              <Flex.Item>
                <Text variant="contentImportant" color="primary-inverse">
                  {currentTitle}
                </Text>
              </Flex.Item>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <Flex>
              {minimizable && (
                <Flex.Item margin="none x-small none none">
                  <IconButton
                    size="small"
                    onClick={handleMinimize}
                    screenReaderLabel={isMinimized ? "Maximize" : "Minimize"}
                    color="primary-inverse"
                  >
                    {isMinimized
                      ? (maximizeIcon || <IconMiniArrowUpLine />)
                      : (minimizeIcon || <IconMiniArrowDownLine />)
                    }
                  </IconButton>
                </Flex.Item>
              )}
              {showMinMax && !isMinimized && (
                <Flex.Item margin="none x-small none none">
                  <IconButton
                    size="small"
                    onClick={handleToggleMaximize}
                    screenReaderLabel={isMaximized ? "Restore" : "Maximize"}
                    color="primary-inverse"
                  >
                    {isMaximized
                      ? <IconExitFullScreenLine />
                      : <IconFullScreenLine />
                    }
                  </IconButton>
                </Flex.Item>
              )}
              <Flex.Item>
                <IconButton
                  size="small"
                  onClick={onClose}
                  screenReaderLabel="Close"
                  color="primary-inverse"
                >
                  {closeIcon || <IconXLine />}
                </IconButton>
              </Flex.Item>
            </Flex>
          </Flex.Item>
        </Flex>
      </div>

      {/* Content — hidden via display:none when minimized to preserve child DOM (e.g. iframes) */}
      <div
        style={{
          display: isMinimized ? 'none' : 'block',
          height: typeof size.height === 'number'
            ? `${size.height - 60}px`
            : `calc(${size.height} - 60px)`,
          overflow: 'auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
          scrollbarWidth: 'thin',
          scrollbarColor: '#c7cdd1 #f5f5f5',
          boxSizing: 'border-box',
          padding: '0',
          margin: '0',
          ...contentStyle
        }}
        className="draggable-modal-content"
      >
        <div style={{ height: '100%' }}>
          {React.createElement(DraggableModalProvider, { value: contextValue, children })}
        </div>
      </div>
    </div>
  );

  // Render at body level when not constrained to container
  if (!constrainToContainer) {
    return createPortal(modalContent, mountNode || document.body);
  }

  return modalContent;
});

DraggableModal.displayName = 'DraggableModal';

export default DraggableModal;
